import React, { useState, useEffect } from 'react';
import { MDBCard, MDBCardBody, MDBTable, MDBTableBody, MDBTableHead, MDBBtn, MDBIcon, MDBBadge, MDBInput } from 'mdb-react-ui-kit';
import { Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../../utils/localizedText';
import { captureError } from '../../utils/sentryHelpers';

const client = generateClient();

/** documentType values consumed by the generatePDFDocument Lambda */
export const DOCUMENT_TYPES = ['invoice', 'remaining-invoice', 'credit'];

const DOCUMENT_TYPE_LABELS = {
  'invoice': 'Invoice',
  'remaining-invoice': 'Remaining Invoice',
  'credit': 'Credit',
};

export default function SystemDocumentSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  // Inline-edit state for the lastNumber quick-edit
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  /** Composite primary key as a stable string for React keys / edit tracking */
  const rowKey = (s) => `${s.documentType}#${s.industryId}`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, industriesRes] = await Promise.all([
        client.graphql({
          query: `
            query ListSystemDocumentSettings {
              listSystemDocumentSettings {
                items {
                  documentType
                  industryId
                  name
                  companyName
                  prefix
                  lastNumber
                  updatedAt
                }
              }
            }
          `
        }),
        client.graphql({
          query: `
            query ListIndustries {
              listIndustries {
                items { id name { language text } }
              }
            }
          `
        }),
      ]);
      setSettings(settingsRes.data.listSystemDocumentSettings.items);
      setIndustries(industriesRes.data.listIndustries.items);
    } catch (error) {
      console.error('Error fetching system document settings:', error);
      captureError(error, {
        feature: 'system-settings-admin',
        operation: 'fetch-document-settings'
      });
    } finally {
      setLoading(false);
    }
  };

  /** Resolves an industryId to its localized name, falling back to the id */
  const industryName = (industryId) => {
    const ind = industries.find((i) => i.id === industryId);
    return ind ? getLocalizedText(ind.name) : industryId;
  };

  const deleteSetting = async (documentType, industryId) => {
    if (!window.confirm(t('Are you sure you want to delete this document setting?'))) return;
    try {
      await client.graphql({
        query: `
          mutation DeleteSystemDocumentSettings($input: DeleteSystemDocumentSettingsInput!) {
            deleteSystemDocumentSettings(input: $input) {
              documentType
            }
          }
        `,
        variables: { input: { documentType, industryId } }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting document setting:', error);
      captureError(error, {
        feature: 'system-settings-admin',
        operation: 'delete-document-settings',
        context: { documentType, industryId }
      });
    }
  };

  const startEdit = (s) => {
    setEditingKey(rowKey(s));
    setEditValue(s.lastNumber ?? '');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  /** Persists the inline-edited lastNumber for a single setting */
  const saveLastNumber = async (s) => {
    const parsed = editValue === '' ? null : parseInt(editValue, 10);
    if (parsed !== null && Number.isNaN(parsed)) return;
    setSavingKey(rowKey(s));
    try {
      await client.graphql({
        query: `
          mutation UpdateSystemDocumentSettings($input: UpdateSystemDocumentSettingsInput!) {
            updateSystemDocumentSettings(input: $input) {
              documentType
              industryId
              lastNumber
            }
          }
        `,
        variables: {
          input: { documentType: s.documentType, industryId: s.industryId, lastNumber: parsed }
        }
      });
      setSettings((prev) =>
        prev.map((row) => (rowKey(row) === rowKey(s) ? { ...row, lastNumber: parsed } : row))
      );
      cancelEdit();
    } catch (error) {
      console.error('Error updating last number:', error);
      captureError(error, {
        feature: 'system-settings-admin',
        operation: 'quick-edit-last-number',
        context: { documentType: s.documentType, industryId: s.industryId }
      });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <MDBCard>
      <MDBCardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{t('Document Settings')}</h5>
          <Link to="/admin/system/document-settings/create">
            <MDBBtn color="primary">
              <MDBIcon icon="plus" className="me-2" /> {t('Add Document Setting')}
            </MDBBtn>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <MDBIcon icon="spinner" spin size="2x" />
          </div>
        ) : (
          <MDBTable hover align="middle">
            <MDBTableHead>
              <tr>
                <th>{t('Document Type')}</th>
                <th>{t('Industry')}</th>
                <th>{t('Name')}</th>
                <th>{t('Company')}</th>
                <th>{t('Prefix')}</th>
                <th>{t('Last Number')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {settings.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    {t('No document settings yet.')}
                  </td>
                </tr>
              )}
              {settings.map((s) => {
                const key = rowKey(s);
                const isEditing = editingKey === key;
                const isSaving = savingKey === key;
                return (
                  <tr key={key}>
                    <td>
                      <MDBBadge color="info" light>
                        {t(DOCUMENT_TYPE_LABELS[s.documentType] || s.documentType)}
                      </MDBBadge>
                    </td>
                    <td>{industryName(s.industryId)}</td>
                    <td>{s.name}</td>
                    <td>{s.companyName}</td>
                    <td>{s.prefix}</td>
                    <td>
                      {isEditing ? (
                        <div className="d-flex align-items-center" style={{ maxWidth: '180px' }}>
                          <MDBInput
                            type="number"
                            size="sm"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveLastNumber(s);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            autoFocus
                          />
                          <MDBBtn
                            color="link"
                            size="sm"
                            className="px-2"
                            disabled={isSaving}
                            onClick={() => saveLastNumber(s)}
                          >
                            <MDBIcon icon={isSaving ? 'spinner' : 'check'} spin={isSaving} className="text-success" />
                          </MDBBtn>
                          <MDBBtn
                            color="link"
                            size="sm"
                            className="px-2"
                            disabled={isSaving}
                            onClick={cancelEdit}
                          >
                            <MDBIcon icon="times" className="text-muted" />
                          </MDBBtn>
                        </div>
                      ) : (
                        <span
                          role="button"
                          className="d-inline-flex align-items-center"
                          onClick={() => startEdit(s)}
                          title={t('Quick edit')}
                        >
                          {s.lastNumber ?? '—'}
                          <MDBIcon icon="pen" size="sm" className="ms-2 text-muted" />
                        </span>
                      )}
                    </td>
                    <td>
                      <Link to={`/admin/system/document-settings/${encodeURIComponent(s.documentType)}/${encodeURIComponent(s.industryId)}`}>
                        <MDBBtn color="link" size="sm">
                          <MDBIcon icon="edit" />
                        </MDBBtn>
                      </Link>
                      <MDBBtn
                        color="link"
                        size="sm"
                        onClick={() => deleteSetting(s.documentType, s.industryId)}
                      >
                        <MDBIcon icon="trash" className="text-danger" />
                      </MDBBtn>
                    </td>
                  </tr>
                );
              })}
            </MDBTableBody>
          </MDBTable>
        )}
      </MDBCardBody>
    </MDBCard>
  );
}
